import type { FC, ReactNode } from 'react';
import { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';

import { RouterLink } from 'src/components/router-link';

interface SideNavItemProps {
  active?: boolean;
  children?: ReactNode;
  depth?: number;
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  label?: ReactNode;
  open?: boolean;
  path?: string;
  title: string;
  onClick?: () => void;
}

export const SideNavItem: FC<SideNavItemProps> = (props) => {
  const {
    active,
    children,
    depth = 0,
    disabled,
    external,
    icon,
    label,
    open: openProp,
    path,
    title
  } = props;
  const [open, setOpen] = useState<boolean>(!!openProp);

  useEffect(() => {
    setOpen(!!openProp);
  }, [openProp]);

  const handleToggle = useCallback(
    (): void => {
      setOpen((prevOpen) => !prevOpen);
    },
    []
  );

  // Icons can be defined at top level only, deep levels have bullets instead of actual icons.

  let startIcon: ReactNode;

  if (depth === 0) {
    startIcon = icon;
  } else {
    startIcon = (
      <Box
        sx={{
          alignItems: 'center',
          display: 'center',
          height: 20,
          justifyContent: 'center',
          width: 20
        }}
      >
        <Box
          sx={{
            backgroundColor: 'var(--nav-item-icon-color)',
            borderRadius: '50%',
            height: 4,
            opacity: 0,
            width: 4,
            ...(active && {
              backgroundColor: 'var(--nav-item-icon-active-color)',
              height: 6,
              opacity: 0,
              width: 6
            })
          }}
        />
      </Box>
    );
  }

  const offset = depth === 0 ? 0 : 6;
  const basePadding = 14;

  // Branch

  if (children) {
    return (
      <li>
        <ButtonBase
          disabled={disabled}
          onClick={() => {
            if (props.onClick) props.onClick();
            handleToggle();
          }}
          sx={{
            alignItems: 'center',
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'flex-start',
            pl: `${basePadding + offset}px`,
            pr: '16px',
            py: '3px',
            textAlign: 'left',
            width: '100%',
            '&:hover': {
              backgroundColor: '#efefef'
            }
          }}
        >
          {startIcon && (
            <Box
              component="span"
              sx={{
                alignItems: 'center',
                color: 'var(--nav-item-icon-color)',
                display: 'inline-flex',
                justifyContent: 'center',
                mr: 1,
                ...(active && {
                  color: 'var(--nav-item-icon-active-color)'
                })
              }}
            >
              {startIcon}
            </Box>
          )}
          <Box
            component="span"
            sx={{
              color: depth > 0 ? '#4c4c4c' : 'var(--nav-item-color)',
              fontFamily: (theme) => theme.typography.fontFamily,
              fontSize: depth > 0 ? 12 : 13,
              fontWeight: depth > 0 ? 400 : 500,
              lineHeight: '24px',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              ...(active && {
                color: 'var(--nav-item-active-color)'
              }),
              ...(disabled && {
                color: 'var(--nav-item-disabled-color)'
              })
            }}
          >
            {title}
            {open ? (
              <ArrowDropDownRoundedIcon sx={{ fontSize: 18, color: 'var(--nav-item-chevron-color)' }} />
            ) : (
              <ArrowRightRoundedIcon sx={{ fontSize: 18, color: 'var(--nav-item-chevron-color)' }} />
            )}
          </Box>
        </ButtonBase>
        <Collapse
          in={open}
          sx={{
            mt: 0.5,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '20px',
              top: 0,
              bottom: 0,
              width: '1px',
              backgroundColor: '#c8c8c8',
              zIndex: 1
            }
          }}
        >
          {children}
        </Collapse>
      </li>
    );
  }

  // Leaf

  const linkProps = path
    ? external
      ? {
        component: 'a',
        href: path,
        target: '_blank'
      }
      : {
        component: RouterLink,
        href: path
      }
    : {};

  return (
    <li>
      <ButtonBase
        disabled={disabled}
        onClick={props.onClick}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'flex-start',
          pl: `${basePadding + offset}px`,
          pr: '16px',
          py: '4px',
          textAlign: 'left',
          width: '100%',
          ...(active && {
            backgroundColor: '#e8e8e8'
          }),
          '&:hover': {
            backgroundColor: active ? '#e8e8e8' : '#efefef'
          }
        }}
        {...linkProps}
      >
        {startIcon && (
          <Box
            component="span"
            sx={{
              alignItems: 'center',
              color: 'var(--nav-item-icon-color)',
              display: 'inline-flex',
              justifyContent: 'center',
              mr: 1,
              ...(active && {
                color: 'var(--nav-item-icon-active-color)'
              })
            }}
          >
            {startIcon}
          </Box>
        )}
        <Box
          component="span"
          sx={{
            color: depth > 0 ? '#4c4c4c' : 'var(--nav-item-color)',
            flexGrow: 1,
            fontFamily: (theme) => theme.typography.fontFamily,
            fontSize: depth > 0 ? 12 : 13,
            fontWeight: depth > 0 ? 500 : 500,
            lineHeight: '24px',
            whiteSpace: 'nowrap',
            ...(active && {
              color: 'var(--nav-item-active-color)'
            }),
            ...(disabled && {
              color: 'var(--nav-item-disabled-color)'
            })
          }}
        >
          {title}
        </Box>
        {label && (
          <Box
            component="span"
            sx={{ ml: 2 }}
          >
            {label}
          </Box>
        )}
      </ButtonBase>
    </li>
  );
};

SideNavItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node as PropTypes.Validator<ReactNode>,
  depth: PropTypes.number,
  disabled: PropTypes.bool,
  external: PropTypes.bool,
  icon: PropTypes.node as PropTypes.Validator<ReactNode>,
  open: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string.isRequired
};
